import React from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import Icon, { IconSizes, IconTypes, IconVariants } from '../../components/foundations/Icon';
import { useLocalStorageState } from '../../utils/customHooks';
import { getCanAccessFeature } from '../../selectors/navigation';
import useAiContextProgress from 'aiContext/hooks/useAiContextProgress';
import { CHAT_EVENTS, trackChatEvent } from '../constants';
import css from './AiContextNudge.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT.EMPTY_STATE.AI_CONTEXT_NUDGE';

export const AI_CONTEXT_NUDGE_DISMISSED_KEY = 'singular-ai-context-nudge-dismissed';
export const AI_CONTEXT_NUDGE_THRESHOLD = 0.3;
export const AI_CONTEXT_NUDGE_SNOOZE_MS = 3 * 24 * 60 * 60 * 1000;  // 3 days
export const AI_CONTEXT_HASH = '#/react/ai-context';

const AiContextNudge = ({ translate }) => {
    const canAccessAiContext = useSelector(getCanAccessFeature('ai_context'));
    const [dismissedAt, setDismissedAt] = useLocalStorageState(AI_CONTEXT_NUDGE_DISMISSED_KEY, null);
    const isSnoozed = typeof dismissedAt === 'number' && Date.now() - dismissedAt < AI_CONTEXT_NUDGE_SNOOZE_MS;
    const { ratio, isLoading, isError } = useAiContextProgress({ enabled: canAccessAiContext && !isSnoozed });

    const shouldShow =
        canAccessAiContext &&
        !isSnoozed &&
        !isLoading &&
        !isError &&
        ratio != null &&
        ratio < AI_CONTEXT_NUDGE_THRESHOLD;

    if (!shouldShow) {
        return null;
    }

    const onCtaClick = () => trackChatEvent(CHAT_EVENTS.AI_CONTEXT_NUDGE_CLICK);

    const onDismiss = () => {
        trackChatEvent(CHAT_EVENTS.AI_CONTEXT_NUDGE_CLOSE);
        setDismissedAt(Date.now());
    };

    return (
        <div className={css.nudge}>
            <div className={css.iconBadge}>
                <Icon
                    name="sparkles"
                    type={IconTypes.TABLER}
                    size={IconSizes.MEDIUM}
                    variant={IconVariants.WHITE}
                    interactive={false}
                />
            </div>
            <div className={css.copy}>
                <div className={css.title}>{translate(`${TRANSLATION_PREFIX}.TITLE`)}</div>
                <div className={css.description}>{translate(`${TRANSLATION_PREFIX}.DESCRIPTION`)}</div>
            </div>
            <a className={css.cta} href={AI_CONTEXT_HASH} onClick={onCtaClick}>
                {translate(`${TRANSLATION_PREFIX}.CTA`)}
            </a>
            <button
                type="button"
                className={css.dismiss}
                aria-label={translate(`${TRANSLATION_PREFIX}.DISMISS_LABEL`)}
                onClick={onDismiss}
            >
                <Icon name="x" type={IconTypes.TABLER} size={IconSizes.SMALL} variant={IconVariants.LIGHT} interactive={false} />
            </button>
        </div>
    );
};

AiContextNudge.propTypes = {
    translate: PropTypes.func.isRequired,
};

export default withLocalize(AiContextNudge);
